/**
 * Allowed HTML tags for rich text: b, br, u, i, em, plus safe links
 * (`<a href>` with scheme check, all other attributes dropped) and inline
 * icon markers (`<span data-icon="name"></span>`). strong is normalized to b;
 * any other span and every other tag is stripped (text kept).
 * Use for paragraphs, titles, accordion content, and any user-editable text.
 *
 * MIRROR of BCC.SDK.Renderers/src/utils/paragraphHtml.ts : the SDK sanitises
 * the same HTML at render time. Keep the two `sanitizeParagraphHtml`
 * implementations byte-identical (a jest parity test compares their outputs
 * over a fixture set). The admin-only `<br>` collapsing happens AFTER the
 * shared core so parity holds on the core output.
 */

/** Valid inline icon name (see the SDK's inlineIcons.ts for the catalogue). */
const ICON_NAME = /^[a-z0-9-]{1,32}$/;

/** NUL sentinel delimiting stashed sanitised tags during the generic pass. */
const NUL = String.fromCharCode(0);

/**
 * Pulls a single attribute value out of a raw tag's attribute string.
 * Matches `name="…"`, `name='…'` and bare `name=value` forms.
 */
function readAttr(attrs: string, name: string): string | null {
  const re = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i");
  const m = re.exec(attrs);
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? "";
}

/** True when an href is safe to keep (no javascript:/vbscript:/data: scheme). */
function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  // Control characters and whitespace are removed before the scheme check so
  // "jav\tascript:" cannot smuggle a scheme past the regex.
  const stripped = Array.from(v)
    .filter((ch) => ch.charCodeAt(0) > 32)
    .join("");
  return !/^(?:javascript|vbscript|data):/i.test(stripped);
}

/** Escapes a string for safe inclusion inside a double-quoted attribute. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function sanitizeParagraphHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";

  // Sanitised anchor/icon tags are stashed as NUL-delimited index tokens so
  // the generic tag pass below cannot re-process (and double-escape) them,
  // and so an unpaired/unsafe <a> or <span> is stripped instead of surviving.
  const tokens: string[] = [];
  const stash = (s: string): string => {
    tokens.push(s);
    return NUL + (tokens.length - 1) + NUL;
  };

  // The NUL delimiter cannot pre-exist in honest input; drop it if present.
  let out = html.split(NUL).join("");

  // PAIRED anchors: kept with a safe href only (all other attributes
  // dropped); unsafe or missing href unwraps the tag and keeps the text.
  out = out.replace(
    /<a\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/a\s*>/gi,
    (_match, attrs: string, inner: string) => {
      const href = readAttr(attrs, "href");
      if (href != null && href.trim() && isSafeHref(href)) {
        return stash(`<a href="${escapeAttr(href.trim())}">`) + inner + stash("</a>");
      }
      return inner;
    },
  );

  // PAIRED icon spans: normalised to an empty `<span data-icon="name"></span>`
  // (children dropped); any other span unwraps to its text.
  out = out.replace(
    /<span\b((?:[^>"']|"[^"]*"|'[^']*')*)>([\s\S]*?)<\/span\s*>/gi,
    (_match, attrs: string, inner: string) => {
      const icon = readAttr(attrs, "data-icon")?.trim().toLowerCase();
      if (icon && ICON_NAME.test(icon)) {
        return stash(`<span data-icon="${icon}"></span>`);
      }
      return inner;
    },
  );

  // Generic pass: keep the simple whitelist (attributes stripped, strong→b);
  // everything else : including stray unpaired <a>/<span> : is removed.
  out = out.replace(/<\/?([a-z][a-z0-9]*)(?:\s[^>]*)?\/?>/gi, (match, tagName: string) => {
    const tag = (tagName || "").toLowerCase();
    if (!/^(?:b|strong|br|u|i|em)$/.test(tag)) return "";
    return match;
  });
  out = out
    .replace(/<(strong)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(strong)>/gi, "</b>")
    .replace(/<(b)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(b)>/gi, "</b>")
    .replace(/<(br)(?:\s[^>]*)?\/?>/gi, "<br>")
    .replace(/<(u)(?:\s[^>]*)?>/gi, "<u>")
    .replace(/<\/(u)>/gi, "</u>")
    .replace(/<(i)(?:\s[^>]*)?>/gi, "<i>")
    .replace(/<\/(i)>/gi, "</i>")
    .replace(/<(em)(?:\s[^>]*)?>/gi, "<em>")
    .replace(/<\/(em)>/gi, "</em>");

  // Restore the sanitised anchor/icon tags.
  out = out.replace(new RegExp(NUL + "(\\d+)" + NUL, "g"), (_m, i: string) => tokens[Number(i)] ?? "");

  // Admin-only post-pass : max 1 empty line: collapse 3+ <br> to 2.
  out = out.replace(/(<br\s*\/?>)\s*(<br\s*\/?>)(\s*<br\s*\/?>)+/gi, "$1$2");
  return out;
}

/** Split plain text by empty lines into paragraphs; each paragraph's newlines become <br>. */
export function splitPlainTextIntoParagraphs(text: string): string[] {
  if (typeof text !== "string" || !text.trim()) return [];
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => block.replace(/\n/g, "<br>"));
}

/** Join HTML paragraphs into plain text: <br> → newline, paragraph boundary = blank line. */
export function paragraphsToPlainText(paragraphs: string[]): string {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return "";
  return paragraphs
    .map((p) => (typeof p === "string" ? p : "").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").trim())
    .join("\n\n");
}

const PARA_SEP = "<br><br>";

/** Join paragraphs into one HTML string for the Raw editor (split by double <br>). */
export function paragraphsToRawHtml(paragraphs: string[]): string {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return "";
  return paragraphs.map((p) => (typeof p === "string" ? p.trim() : "")).join(PARA_SEP);
}

/** Split Raw editor HTML by double <br> into paragraphs; sanitize each. */
export function rawHtmlToParagraphs(html: string): string[] {
  if (typeof html !== "string" || !html.trim()) return [];
  const parts = html.split(/(?:<br\s*\/?>\s*){2,}/i).map((s) => s.trim()).filter(Boolean);
  return parts.map((p) => sanitizeParagraphHtml(p));
}

/** Keys that may contain rich HTML; sanitize at save time. Includes accordion item titles, section headings, etc. */
const RICH_TEXT_KEYS = new Set(["title", "text", "body", "subtitle", "panelTitle", "description", "introParagraph"]);

function sanitizeContentValue(value: unknown): unknown {
  if (typeof value === "string") return sanitizeParagraphHtml(value);
  if (Array.isArray(value)) return value.map(sanitizeContentValue);
  if (value !== null && typeof value === "object") return sanitizeContentObject(value as Record<string, unknown>);
  return value;
}

function sanitizeContentObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "paragraphs" && Array.isArray(value)) {
      out[key] = value.map((item) => (typeof item === "string" ? sanitizeParagraphHtml(item) : item));
    } else if (RICH_TEXT_KEYS.has(key) && typeof value === "string") {
      out[key] = sanitizeParagraphHtml(value);
    } else {
      out[key] = sanitizeContentValue(value);
    }
  }
  return out;
}

/**
 * Sanitize all rich-text fields in page components at save time.
 * - Paragraphs arrays (e.g. TextBlock, AccordionSection items): each string is sanitized; <span> and disallowed tags are stripped.
 * - Title/text/body/subtitle/panelTitle/description/introParagraph: sanitized (including accordion item titles).
 * - Nested structures (e.g. content.items[].title, content.items[].paragraphs) are recursed into.
 */
export function sanitizePageComponentsForSave(components: unknown[]): unknown[] {
  if (!Array.isArray(components)) return components;
  return components.map((comp) => {
    if (comp === null || typeof comp !== "object") return comp;
    const c = comp as { componentType?: string; key?: string; content?: unknown };
    if (c.content === null || typeof c.content !== "object") return comp;
    let content = sanitizeContentObject(c.content as Record<string, unknown>);
    if (c.componentType === "SectionHeading" && typeof content.text === "string") {
      content = { ...content, text: content.text.replace(/\r?\n/g, " ").trim() };
    }
    // ArticleHero: do not persist shareLabel/shareOptions (fixed in renderer: "Partager", all social on)
    if (c.componentType === "ArticleHero" && content && typeof content === "object") {
      const { shareLabel: _sl, shareOptions: _so, ...rest } = content as Record<string, unknown>;
      content = rest;
    }
    return { ...c, content };
  });
}
